import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";

import {
  StzhDatatableCellClickEvent,
  StzhDatatableCellData,
  StzhDatatableColumnData,
  StzhDatatableHeadingCellClickEvent,
  StzhDatatableInputRowData,
  StzhDatatableRowClickEvent,
  StzhDatatableRowData,
  StzhDatatableSortByChangeEvent,
  StzhDatatableSortClickEvent,
  StzhDatatableSortDirectionChangeEvent,
  StzhDropdownChangeEvent,
  StzhPaginationChangeEvent,
} from "../../index";

import { Document } from "flexsearch/dist/flexsearch.bundle.min.js";
import { parse } from "papaparse";

import { StzhDatatableLocalizedText } from "./stzh-datatable.localization";
import { hasSlot } from "../../utils/utils";

/**
 * @slot heading - Slot for heading element
 * @slot action-button - Slot for action `stzh-button` element
 */
@Component({
  tag: "stzh-datatable",
  styleUrl: "stzh-datatable.scss",
})
export class StzhDatatable {
  /** Table layout */
  @Prop() tableLayout: "auto" | "fixed" = "auto";

  /** Translation strings */
  @Prop() localization: StzhDatatableLocalizedText;

  /** Label */
  @Prop() label: string = "";

  /** Caption for screenreader */
  @Prop() caption: string = "";

  /** Sort by given column key */
  @Prop({ mutable: true }) sortBy: string = "";

  /** Sort direction */
  @Prop({ mutable: true }) sortDirection: "asc" | "desc" = "asc";

  /** Tokenize type */
  // @Prop() tokenize: "strict" | "forward" | "reverse" | "full" = "forward";

  /** ID field */
  @Prop() idField = "id";

  /** Row header field when list is used (`showList=true`) */
  @Prop() rowHeaderField = "";

  /* Whether to hide search */
  @Prop({ reflect: true }) hideSearch = false;

  /* Whether to hide column heading cells */
  @Prop({ reflect: true }) hideColumnHeadings = false;

  /** Whether to show table data as list on mikro breakpoint */
  @Prop({ reflect: true }) showList = false;

  /** Current page */
  @Prop({ mutable: true }) page: number = 1;

  /** If pagination should be shown, set a number of items to be shown by each page. */
  @Prop() pageSize: number = null;

  /** URL of the CSV data */
  @Prop() csvUrl: string;

  /** Whether csv has header row */
  @Prop() csvHeader: boolean = true;

  /** Whether csv has dynamic typing enabled */
  @Prop() csvDynamicTyping: boolean = true;

  /**
   * Whether to disable min-width of the table.
   *
   * If `false`, the default `min-width` is `700px`
   * If `true`, the `min-width` is `none`, so the table will try to squeeze down content as much as possible.
   *
   * You can also overwrite the min-width via CSS with the custom property `--min-width`.
   */
  @Prop({ reflect: true }) disableMinWidth: boolean = false;

  /**
   * Array of objects including the row data that must at least include `id` attribute (name can be changed with `idField` property).
   *
   * Example: [{"id":"1","text":"Foo"}, ...]
   */
  @Prop({ mutable: true }) rows: StzhDatatableInputRowData[] | StzhDatatableCellData[][] | string = [];
  private _rows: StzhDatatableRowData[];
  private _rowsShown: StzhDatatableRowData[];
  private _rowsFiltered: StzhDatatableRowData[];

  /**
   * Array of objects including the columns data that must include `key` and `text` attribute.
   *
   * Example: [{ "key": "id", "text": "ID", "align": "right", "sort": true, "fixed": "right", style: { width: "53px" } }]
   */

  @Prop({ mutable: true }) columns: StzhDatatableColumnData[] | string = [];
  private _columns: StzhDatatableColumnData[];

  @State() query: string = "";
  @State() hasScrollLeft: boolean = false;
  @State() hasScrollRight: boolean = false;
  @State() isScrollable: boolean = false;
  @State() cellsStickyLeft: number[] = [];
  @State() cellsStickyRight: number[] = [];

  @Element() element: HTMLStzhDatatableElement;

  /** Datatable row click event */
  @Event() stzhRowClick: EventEmitter<StzhDatatableRowClickEvent>;

  /** Datatable cell click event */
  @Event() stzhCellClick: EventEmitter<StzhDatatableCellClickEvent>;

  /** Datatable heading cell click event */
  @Event() stzhHeadingCellClick: EventEmitter<StzhDatatableHeadingCellClickEvent>;

  /** Datatable sort click event */
  @Event() stzhSortClick: EventEmitter<StzhDatatableSortClickEvent>;

  /** Datatable sort by change event */
  @Event() stzhSortByChange: EventEmitter<StzhDatatableSortByChangeEvent>;

  /** Datatable sort direction change event */
  @Event() stzhSortDirectionChange: EventEmitter<StzhDatatableSortDirectionChangeEvent>;

  /** Toggle sort by a given column key like when clicking on a column header */
  @Method()
  async toggleSort(columnKey: string) {
    this.page = 1;

    if (columnKey === this.sortBy) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortDirection = "asc";
      this.sortBy = columnKey;
    }
  }

  @Watch("csvUrls")
  async csvUrlsWatcher(newUrl: string) {
    if (!newUrl) {
      return;
    }

    const response = await fetch(newUrl);
    const csvData = await response.text();
    const { columns, rows } = await this.parseCsvToJson(csvData);

    if (this.csvHeader) {
      this.columns = columns.map(column => ({
        ...column,
        ...(this._columns?.find(({ key }) => key === column.key) || {}),
      }));
    } else {
      this.columns = this.columns || columns;
    }

    this.rows = rows;
  }

  @Watch("rows")
  rowsWatcher(newValue: StzhDatatableInputRowData[] | StzhDatatableCellData[][] | string) {
    let inputData: StzhDatatableInputRowData[] | StzhDatatableCellData[][];

    if (typeof newValue === "string") {
      inputData = JSON.parse(newValue);
    } else {
      inputData = newValue;
    }

    this._rows = inputData.map((rowData: StzhDatatableCellData[] | StzhDatatableInputRowData) => {
      const row: StzhDatatableRowData = {};

      function transformRowCell(cellData: StzhDatatableCellData | string | number) {
        const baseCellData: StzhDatatableCellData = {
          value: "",
        };

        if (typeof cellData === "object") {
          return {
            ...baseCellData,
            ...cellData,
          };
        } else {
          return {
            ...baseCellData,
            value: cellData,
          };
        }
      }

      this._columns.forEach((column, index) => {
        if (Array.isArray(rowData)) {
          row[column.key] = transformRowCell(rowData[index]);
        } else {
          row[column.key] = transformRowCell(rowData[column.key]);
        }
      });

      return row;
    });

    this.buildSearchIndex();
  }

  @Watch("columns")
  columnsWatcher(newValue: StzhDatatableColumnData[] | string) {
    if (typeof newValue === "string") {
      this._columns = JSON.parse(newValue);
    } else {
      this._columns = newValue;
    }

    this.rowsWatcher(this.rows);
  }

  @Watch("sortBy")
  sortByWatcher(value: string, oldValue: string) {
    this.stzhSortByChange.emit({
      component: "stzh-datatable",
      value,
      oldValue,
    });
  }

  @Watch("sortDirection")
  sortDirectionWatcher(value: "asc" | "desc", oldValue: "asc" | "desc") {
    this.stzhSortDirectionChange.emit({
      component: "stzh-datatable",
      value,
      oldValue,
    });
  }

  private input: HTMLStzhInputElement;
  private searchIndex: Document;
  private tableWrapper: HTMLDivElement;
  private debounceScroll: number;

  private debounceResize: number;
  private resizeObserver: ResizeObserver;

  private async parseCsvToJson(csvData: string): Promise<{
    columns: { key: string; text: string }[];
    rows: { [key: string]: string | number }[];
  }> {
    return new Promise((resolve, reject) => {
      parse(csvData, {
        header: this.csvHeader,
        dynamicTyping: this.csvDynamicTyping,
        complete: result => {
          const normalizedData = result.data.map(row => {
            const normalizedRow = {};
            Object.keys(row).forEach(key => {
              normalizedRow[key.toLowerCase()] = row[key];
            });
            return normalizedRow;
          });
          resolve({
            columns: Object.keys(result.data[0]).map(key => ({
              key: key.toLowerCase(),
              text: key,
            })),
            rows: normalizedData,
          });
        },
        error: error => {
          reject(error);
        },
      });
    });
  }

  private handleSortByDropdownChange = async (event: CustomEvent<StzhDropdownChangeEvent>) => {
    const target: HTMLStzhDropdownElement = event.target as HTMLStzhDropdownElement;
    const sortBy = (await target.getValue()) as string;
    this.sortBy = sortBy;
  };

  private handleSortDirectionDropdownChange = async (event: CustomEvent<StzhDropdownChangeEvent>) => {
    const target: HTMLStzhDropdownElement = event.target as HTMLStzhDropdownElement;
    const sortDirection = (await target.getValue()) as "asc" | "desc";
    this.sortDirection = sortDirection;
  };

  private handleRowClick = (event: MouseEvent, row: StzhDatatableRowData) => {
    this.stzhRowClick.emit({
      component: "stzh-datatable",
      row,
      originalEvent: event,
    });
  };

  private handleCellClick = (
    event: MouseEvent,
    row: StzhDatatableRowData,
    column: StzhDatatableColumnData,
    cellIndex: number,
    rowIndex: number
  ) => {
    this.stzhCellClick.emit({
      component: "stzh-datatable",
      row,
      column,
      cellIndex,
      rowIndex,
      originalEvent: event,
    });
  };

  private handleHeadingCellClick(event: MouseEvent, column: StzhDatatableColumnData, index: number) {
    this.stzhHeadingCellClick.emit({
      component: "stzh-datatable",
      column,
      index,
      originalEvent: event,
    });
  }

  private handleSortClick = (event: MouseEvent, column: StzhDatatableColumnData) => {
    this.stzhSortClick.emit({
      component: "stzh-datatable",
      column,
      originalEvent: event,
    });

    this.toggleSort(column.key);
  };

  private updateScrollStates = () => {
    if (!this.tableWrapper) {
      return;
    }

    const wrapperScrollLeft = this.tableWrapper.scrollLeft;
    const wrapperScrollWidth = this.tableWrapper.scrollWidth;
    const wrapperClientWidth = this.tableWrapper.clientWidth;

    this.isScrollable = wrapperScrollWidth > wrapperClientWidth;
    this.hasScrollLeft = wrapperScrollLeft > 0;
    this.hasScrollRight = wrapperScrollLeft < wrapperScrollWidth - wrapperClientWidth;

    const rows: HTMLTableRowElement[] = Array.from(this.tableWrapper.querySelectorAll("tr"));
    const firstRow = rows[0];

    if (!firstRow) {
      return;
    }

    this.cellsStickyLeft = [];
    this.cellsStickyRight = [];
    const cells: Element[] = Array.from(firstRow.children);

    cells.forEach((cell: HTMLTableCellElement, index: number) => {
      const offsetLeft = cell.offsetLeft;
      const offsetRight = offsetLeft + cell.offsetWidth;

      if (this.hasScrollLeft && this._columns[index].fixed === "left" && Math.ceil(wrapperScrollLeft) >= offsetLeft) {
        this.cellsStickyLeft.push(index);
      }

      if (
        this.hasScrollRight &&
        this._columns[index].fixed === "right" &&
        Math.ceil(wrapperScrollLeft) + wrapperClientWidth <= offsetRight
      ) {
        this.cellsStickyRight.push(index);
      }
    });
  };

  private handleResize = () => {
    if (this.debounceResize) {
      window.cancelAnimationFrame(this.debounceResize);
    }

    this.debounceResize = requestAnimationFrame(this.updateScrollStates);
  };

  private handleScroll = () => {
    if (this.debounceScroll) {
      window.cancelAnimationFrame(this.debounceScroll);
    }

    this.debounceScroll = requestAnimationFrame(this.updateScrollStates);
  };

  private buildSearchIndex() {
    if (!this._rows || this._rows.length === 0 || !this._columns || this._columns.length === 0) {
      return;
    }

    this.searchIndex = new Document({
      tokenize: "forward",
      matcher: {
        "[äà]": "a",
        "[öó]": "o",
        "[üûúù]": "u",
        "[éè]": "e",
      },
      document: {
        id: this.idField,
        index: this._columns.map(column => column.key),
      },
    });

    this._rows.forEach(row => {
      const rowData: {
        [key: string]: string;
      } = {};

      for (const columnKey in row) {
        rowData[columnKey] = row[columnKey].value.toString();
      }

      this.searchIndex.add(rowData);
    });
  }

  private onInputChange = () => {
    this.query = this.input.value;
  };

  private onPaginationChange = (event: CustomEvent<StzhPaginationChangeEvent>) => {
    if (event.detail.component === "stzh-pagination") {
      this.page = event.detail.page;
    }
  };

  async componentWillLoad() {
    this.columnsWatcher(this.columns);
    await this.csvUrlsWatcher(this.csvUrl);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "datatable");
    }
  }

  componentDidLoad() {
    this.updateScrollStates();
  }

  componentWillRender() {
    this._rowsShown = [];
    this._rowsFiltered = [];

    if (this.query) {
      let resultIds = [];

      this.searchIndex.search(this.query).forEach(result => {
        resultIds = [...resultIds, ...result.result];
      });

      resultIds = resultIds.filter(function (item, pos) {
        return resultIds.indexOf(item) === pos;
      });

      this._rows.forEach(row => {
        if (resultIds.includes(row[this.idField].value) || resultIds.includes(row[this.idField].value.toString())) {
          this._rowsFiltered.push(row);
        }
      });
    } else {
      this._rowsFiltered = [...this._rows];
    }

    if (this.sortBy) {
      const columnConfig = this._columns.find(column => column.key === this.sortBy);

      let column = this.sortBy;
      let type = null;

      if (typeof columnConfig.sort === "object") {
        if (columnConfig.sort.field) {
          column = columnConfig.sort.field;
        }

        if (columnConfig.sort.type) {
          type = columnConfig.sort.type;
        }
      }

      this._rowsFiltered.sort((rowA, rowB) => {
        if (type === "numerical") {
          if (this.sortDirection === "asc") {
            return Number(rowA[column].value) - Number(rowB[column].value);
          } else {
            return Number(rowB[column].value) - Number(rowA[column].value);
          }
        } else {
          const comparison = rowA[column].value.toString().localeCompare(rowB[column].value.toString());
          return this.sortDirection === "asc" ? comparison : comparison / -1;
        }
      });
    }

    if (this.pageSize !== null) {
      const pageEnd = this.page * this.pageSize;
      this._rowsShown = this._rowsFiltered.slice(pageEnd - this.pageSize, pageEnd);
    } else {
      this._rowsShown = [...this._rowsFiltered];
    }
  }

  connectedCallback() {
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.element);
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  render() {
    const headingSlotUsed = hasSlot(this.element, "heading");
    const actionButtonSlotUsed = hasSlot(this.element, "action-button");

    const classes = {
      "stzh-datatable": true,
      "is-scrollable": this.isScrollable,
      "has-scroll-left": this.hasScrollLeft,
      "has-scroll-right": this.hasScrollRight,
      "has-scroll-left-shadow": this.hasScrollLeft && this.cellsStickyLeft.length === 0,
      "has-scroll-right-shadow": this.hasScrollRight && this.cellsStickyRight.length === 0,
      "has-list": this.showList,
      "has-heading": headingSlotUsed,
      "has-action-or-search": actionButtonSlotUsed || !this.hideSearch,
      "has-header": headingSlotUsed || actionButtonSlotUsed || !this.hideSearch,
    };

    const totalPages = Math.ceil(this._rowsFiltered.length / this.pageSize);

    const firstHeaderColumn = this._columns.find(column => column.isHeader);

    const sortByOptions = [
      {
        text: this.localization.sortByDefaultOption,
        value: "",
      },
      ...this._columns
        .filter(column => !!column.sort)
        .map(column => ({
          value: column.key,
          text: this.localization.sortByOption?.replace(/\{column\}/gi, column.text),
        })),
    ];

    const sortDirectionOptions = [
      {
        value: "asc",
        text: this.localization.sortDirectionOption?.replace(/\{direction\}/gi, this.localization.asc),
      },
      {
        value: "desc",
        text: this.localization.sortDirectionOption?.replace(/\{direction\}/gi, this.localization.desc),
      },
    ];

    const getCellClasses = (column: StzhDatatableColumnData, row: StzhDatatableRowData, index: number) => {
      return {
        "stzh-datatable__cell stzh-datatable__cell--body": true,
        "is-sticky-left": column.fixed === "left",
        "is-sticky-right": column.fixed === "right",
        "is-head": !!row[column.key]?.isHeader || !!column.isHeader,
        "is-sortable": !!column.sort,
        "has-sticked": this.cellsStickyLeft.indexOf(index) > -1 || this.cellsStickyRight.indexOf(index) > -1,
        "has-sorted": column.key === this.sortBy,
        "has-sorted-asc": column.key === this.sortBy && this.sortDirection === "asc",
        "has-sorted-desc": column.key === this.sortBy && this.sortDirection === "desc",
        [`align-${row[column.key]?.align || column.align}`]: !!row[column.key]?.align || !!column.align,
        [`valign-${row[column.key]?.valign || column.valign}`]: !!row[column.key]?.valign || !!column.valign,
      };
    };

    return (
      <Host>
        <div class={classes}>
          <div class="stzh-datatable__header">
            <div class="stzh-datatable__heading">
              <slot name="heading"></slot>
            </div>
            <div class="stzh-datatable__sub-header">
              {!this.hideSearch && (
                <stzh-input
                  ref={el => (this.input = el as HTMLStzhInputElement)}
                  class="stzh-datatable__search"
                  size="small"
                  name="stzh-datatable-search"
                  onStzhChange={this.onInputChange}
                  hideOptional={true}
                  label={this.localization.searchLabel}
                  type="search"
                ></stzh-input>
              )}
              <div class="stzh-datatable__action-button">
                <slot name="action-button"></slot>
              </div>
            </div>
          </div>

          <div class="stzh-datatable__outer-wrapper">
            <div
              ref={el => (this.tableWrapper = el as HTMLDivElement)}
              class="stzh-datatable__wrapper"
              onScroll={this.handleScroll}
            >
              <table
                class={{
                  "stzh-datatable__table": true,
                  "has-layout-fixed": this.tableLayout === "fixed",
                }}
              >
                {!!this.caption && <caption class="stzh-datatable__caption">{this.caption}</caption>}
                {!this.hideColumnHeadings && (
                  <thead class="stzh-datatable__thead">
                    <tr class="stzh-datatable__row stzh-datatable__row--head">
                      {(() => {
                        let colspanOffset = 0;
                        return this._columns.map((column, index) => {
                          if (colspanOffset > 0) {
                            colspanOffset--;
                            return null;
                          }

                          const colspan = column.colspan || 1;
                          if (colspan > 1) {
                            colspanOffset = colspan - 1;
                          }

                          return (
                            <th
                              onClick={event => this.handleHeadingCellClick(event, column, index)}
                              style={column.style}
                              class={{
                                "stzh-datatable__cell stzh-datatable__cell--head": true,
                                "is-sticky-left": column.fixed === "left",
                                "is-sticky-right": column.fixed === "right",
                                "is-sortable": !!column.sort,
                                "has-sticked":
                                  this.cellsStickyLeft.indexOf(index) > -1 || this.cellsStickyRight.indexOf(index) > -1,
                                "has-sorted": column.key === this.sortBy,
                                "has-sorted-asc": column.key === this.sortBy && this.sortDirection === "asc",
                                "has-sorted-desc": column.key === this.sortBy && this.sortDirection === "desc",
                                [`align-${column.align}`]: !!column.align,
                                [`valign-${column.valign}`]: !!column.valign,
                              }}
                              {...(colspan > 1 ? { colspan } : {})}
                              {...(column.rowspan ? { rowspan: column.rowspan } : {})}
                            >
                              {!column.hideColumnHeading &&
                                (column.sort ? (
                                  <button
                                    class="stzh-datatable__sort-button"
                                    type="button"
                                    onClick={event => this.handleSortClick(event, column)}
                                    s-object-id={column.analyticsId || `${column.text} ${this.localization.columnSort}`}
                                  >
                                    <span>{column.text}</span>
                                    <stzh-icon
                                      name="arrow-up"
                                      class="stzh-datatable__sort-icon"
                                      a11yTitle={this.localization.columnSort}
                                    ></stzh-icon>
                                  </button>
                                ) : (
                                  <span>{column.text}</span>
                                ))}
                            </th>
                          );
                        });
                      })()}
                    </tr>
                  </thead>
                )}
                <tbody class="stzh-datatable__tbody">
                  {(() => {
                    const rowspanOffsets = [];

                    return this._rowsShown.map((row, rowIndex) => (
                      <tr
                        class="stzh-datatable__row stzh-datatable__row--body"
                        onClick={event => this.handleRowClick(event, row)}
                      >
                        {(() => {
                          let colspanOffset = 0;
                          return this._columns.map((column, index) => {
                            if (rowspanOffsets[index]) {
                              rowspanOffsets[index]--;
                              return null;
                            }
                            if (colspanOffset > 0) {
                              colspanOffset--;
                              return null;
                            }

                            const cell = row[column.key];
                            const colspan = cell?.colspan || 1;
                            const rowspan = cell?.rowspan || 1;

                            if (colspan > 1) {
                              colspanOffset = colspan - 1;
                            }
                            if (rowspan > 1) {
                              rowspanOffsets[index] = rowspan - 1;
                            }

                            return cell?.isHeader || column.isHeader ? (
                              <th
                                id={cell.id}
                                onClick={event => this.handleCellClick(event, row, column, index, rowIndex)}
                                innerHTML={cell.value.toString() || ""}
                                style={column.style}
                                {...(colspan > 1 ? { colspan } : {})}
                                {...(rowspan > 1 ? { rowspan } : {})}
                                class={getCellClasses(column, row, index)}
                              ></th>
                            ) : (
                              <td
                                id={cell.id}
                                onClick={event => this.handleCellClick(event, row, column, index, rowIndex)}
                                innerHTML={cell.value.toString() || ""}
                                style={column.style}
                                {...(colspan > 1 ? { colspan } : {})}
                                {...(rowspan > 1 ? { rowspan } : {})}
                                class={getCellClasses(column, row, index)}
                              ></td>
                            );
                          });
                        })()}
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {this.showList && (
            <div class="stzh-datatable__list-wrapper">
              {sortByOptions.length > 1 && (
                <div class="stzh-datatable__list-sort-wrapper">
                  <stzh-dropdown
                    inline
                    no-search
                    class="stzh-datatable__list-sort-by-dropdown"
                    variant="plain"
                    size="small"
                    items={[this.sortBy]}
                    options={sortByOptions}
                    onStzhChange={this.handleSortByDropdownChange}
                  ></stzh-dropdown>
                  <stzh-dropdown
                    disabled={this.sortBy === ""}
                    inline
                    no-search
                    class="stzh-datatable__list-sort-direction-dropdown"
                    variant="plain"
                    size="small"
                    items={[this.sortDirection]}
                    options={sortDirectionOptions}
                    onStzhChange={this.handleSortDirectionDropdownChange}
                  ></stzh-dropdown>
                </div>
              )}

              <stzh-accordion variant="table">
                {this._rowsShown.map((row, index) => (
                  <stzh-accordion-item
                    heading={
                      row[this.rowHeaderField]?.value?.toString() ||
                      row[firstHeaderColumn?.key]?.value?.toString() ||
                      this.localization.defaultListRowHeading.replace(/\{n\}/gi, (index + 1).toString())
                    }
                  >
                    <stzh-datalist variant="table" columnsMedium={2}>
                      {this._columns.map(column => (
                        <stzh-datalist-item
                          label={column.text}
                          value={row[column.key]?.value?.toString() || ""}
                        ></stzh-datalist-item>
                      ))}
                    </stzh-datalist>
                  </stzh-accordion-item>
                ))}
              </stzh-accordion>
            </div>
          )}

          {this.pageSize !== null && totalPages > 1 && (
            <div class="stzh-datatable__pagination-wrapper">
              <stzh-pagination
                value={this.page}
                total={totalPages}
                onStzhChange={this.onPaginationChange}
              ></stzh-pagination>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
